import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Shield, Plus, Minus, History, Edit } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDateTime } from '../lib/dateUtils';

export default function SafetyPointsPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'qhse');

  const [points, setPoints] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChange, setShowChange] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEditReason, setShowEditReason] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [changeForm, setChangeForm] = useState({ employee_id: '', change: '', reason: '' });

  const load = useCallback(async () => {
    try {
      const [pRes, eRes] = await Promise.all([
        api.get('/safety-points'),
        canManage ? api.get('/employees').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setPoints(pRes.data);
      setEmployees(eRes.data.filter(e => e.is_active));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  const handleChange = async () => {
    if (!changeForm.employee_id || !changeForm.change) {
      alert('Compila tutti i campi');
      return;
    }
    try {
      await api.post('/safety-points', {
        employee_id: changeForm.employee_id,
        change: parseInt(changeForm.change),
        reason: changeForm.reason,
      });
      setShowChange(false);
      setChangeForm({ employee_id: '', change: '', reason: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openHistory = async (emp) => {
    setSelectedEmp(emp);
    try {
      const res = await api.get(`/safety-points/history/${emp.employee_id}`);
      setHistoryData(res.data);
      setShowHistory(true);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openEditReason = (item) => {
    setEditItem(item);
    setEditReason(item.reason || '');
    setShowEditReason(true);
  };

  const handleEditReason = async () => {
    try {
      await api.put(`/safety-points/history/${editItem.id}`, { reason: editReason });
      setShowEditReason(false);
      if (selectedEmp) {
        const res = await api.get(`/safety-points/history/${selectedEmp.employee_id}`);
        setHistoryData(res.data);
      }
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const pointsColor = (p) => {
    if (p >= 12) return 'text-green-600';
    return 'text-orange-500';
  };

  const pointsBg = (p) => {
    if (p >= 12) return 'bg-green-50 border-green-200';
    return 'bg-orange-50 border-orange-200';
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Caricamento...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="safety-points-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Punti Sicurezza</h1>
        {canManage && (
          <Button onClick={() => setShowChange(true)} data-testid="add-points-btn">
            <Plus size={16} className="mr-1" /> Aggiungi/Sottrai Punti
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {points.map(p => (
          <Card key={p.id} className={`border shadow-sm cursor-pointer hover:shadow-md transition-shadow ${pointsBg(p.current_points)}`}
            onClick={() => openHistory(p)} data-testid={`sp-card-${p.employee_id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border">
                <Shield size={20} className={pointsColor(p.current_points)} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm text-slate-900">{p.employee_name}</p>
                <p className={`text-2xl font-bold ${pointsColor(p.current_points)}`}>{p.current_points} <span className="text-xs font-normal text-slate-500">/ {PUNTI_INIZIALI}</span></p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Change Dialog */}
      <Dialog open={showChange} onOpenChange={setShowChange}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifica Punti Sicurezza</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Dipendente *</Label>
              <Select value={changeForm.employee_id} onValueChange={v => setChangeForm(prev => ({ ...prev, employee_id: v }))}>
                <SelectTrigger data-testid="sp-employee"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Punti (+ aggiunge, - sottrae) *</Label>
              <Input type="number" value={changeForm.change} onChange={e => setChangeForm(prev => ({ ...prev, change: e.target.value }))} placeholder="es. -2 o +1" data-testid="sp-change" />
            </div>
            <div className="space-y-1.5">
              <Label>Motivazione *</Label>
              <Textarea value={changeForm.reason} onChange={e => setChangeForm(prev => ({ ...prev, reason: e.target.value }))} placeholder="Motivazione dell'addizione/sottrazione..." data-testid="sp-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChange(false)}>Annulla</Button>
            <Button onClick={handleChange} data-testid="sp-confirm">Applica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Storico Punti - {selectedEmp?.employee_name}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead className="text-center">Variazione</TableHead>
                <TableHead className="text-center">Totale</TableHead>
                <TableHead>Motivazione</TableHead>
                {canManage && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyData.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Nessun movimento</TableCell></TableRow>
              ) : historyData.map(h => (
                <TableRow key={h.id}>
                  <TableCell className="text-sm">{fmtDateTime(h.created_at)}</TableCell>
                  <TableCell className="text-center text-sm font-bold">
                    <span className={h.change > 0 ? 'text-green-600' : 'text-red-600'}>
                      {h.change > 0 ? '+' : ''}{h.change}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-sm font-bold">{h.new_total}</TableCell>
                  <TableCell className="text-sm text-slate-600">{h.reason || '-'}</TableCell>
                  {canManage && (
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => openEditReason(h)} data-testid={`edit-reason-${h.id}`}>
                        <Edit size={14} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Edit Reason Dialog */}
      <Dialog open={showEditReason} onOpenChange={setShowEditReason}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifica Motivazione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Motivazione</Label>
              <Textarea value={editReason} onChange={e => setEditReason(e.target.value)} data-testid="edit-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditReason(false)}>Annulla</Button>
            <Button onClick={handleEditReason} data-testid="edit-reason-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PUNTI_INIZIALI = 20;
